import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import useMediaQuery from '@mui/material/useMediaQuery';
import { capitalise } from '../../utils/stringUtil';
import { formatCurrency } from '../../utils/currencyUtil';
import {
  Payment,
  useGetPaymentsUserQuery,
  usePostPaymentsReconcilePendingMutation,
} from '../../redux/residentPortalApi';
import { makeStyles } from '@mui/styles';
import { theme } from '../../theme/theme';
import ResponsiveRecordCard from '../common/ResponsiveRecordCard';

interface OwnProps {
  propertyId: string;
}

const useStyles = makeStyles({
  activeStatusIcon: {
    color: 'green',
  },
  inactiveStatusIcon: {
    color: 'orange',
  },
  failedStatusIcon: {
    color: 'red',
  },
});

const isPadelPayment = (payment: Payment) => {
  const purpose = (payment.purpose || '').toLowerCase();
  return (
    purpose === 'pedalcourtinvoicebalance' ||
    purpose === 'pedal court invoice balance' ||
    purpose === 'pedal court invoice' ||
    purpose === 'pedal court balance' ||
    purpose === 'padel court invoice balance' ||
    purpose === 'padel court invoice' ||
    purpose === 'padel court balance' ||
    purpose === 'pedal court' ||
    purpose === 'padel court' ||
    purpose.includes('padel') ||
    purpose.includes('pedal')
  );
};

const PadelCourtPaymentHistory: React.FC<OwnProps> = ({ propertyId }) => {
  const classes = useStyles();
  const showCards = useMediaQuery(theme.breakpoints.down('md'));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [historyFilter, setHistoryFilter] = useState('');

  const { data: allPayments = [], isLoading, refetch } = useGetPaymentsUserQuery(
    { propertyId },
    { skip: !propertyId }
  );
  const [reconcilePendingPayments] = usePostPaymentsReconcilePendingMutation();

  useEffect(() => {
    if (!propertyId) return;

    let isCancelled = false;

    const reconcilePayments = async () => {
      try {
        const result = await reconcilePendingPayments({
          body: { propertyId },
        }).unwrap();

        if (!isCancelled && (result.updatedCount ?? 0) > 0) {
          await refetch();
        }
      } catch (error) {
        console.error('Failed to reconcile pending payments for padel court:', error);
      }
    };

    void reconcilePayments();

    return () => {
      isCancelled = true;
    };
  }, [propertyId, reconcilePendingPayments, refetch]);

  const padelPayments = useMemo(() => {
    return allPayments.filter(isPadelPayment);
  }, [allPayments]);

  const filteredPayments = useMemo(() => {
    const searchTerm = historyFilter.trim().toLowerCase();

    if (!searchTerm) {
      return padelPayments;
    }

    return padelPayments.filter((payment) => {
      const haystack = [
        payment.paymentMethod,
        payment.purpose,
        payment.paymentIntentId,
        payment.paymentDate,
        payment.status,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(searchTerm);
    });
  }, [historyFilter, padelPayments]);

  useEffect(() => {
    setPage(0);
  }, [filteredPayments.length]);

  const paginatedPayments = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredPayments.slice(start, start + rowsPerPage);
  }, [filteredPayments, page, rowsPerPage]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status?: string) => {
    const st = (status || '').toLowerCase();
    if (['succeeded', 'paid', 'success', 'completed'].includes(st)) return 'success';
    if (['pending', 'processing'].includes(st)) return 'warning';
    return 'error';
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 4,
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(0, 0, 0, 0.05)',
        mb: 4,
      }}
    >
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
        >
          <Typography
            variant="h2"
            sx={{
              fontSize: '1.75rem',
              fontWeight: 600,
              color: '#1976d2',
              borderBottom: '2px solid #e0e0e0',
              paddingBottom: 1,
            }}
          >
            Padel Court Payments History
          </Typography>

          <TextField
            size="small"
            placeholder="Search payments..."
            value={historyFilter}
            onChange={(e) => setHistoryFilter(e.target.value)}
            sx={{ minWidth: 220 }}
          />
        </Stack>

        {filteredPayments.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body1">
              {isLoading ? 'Loading payment history...' : 'No Padel Court payments found.'}
            </Typography>
          </Box>
        ) : showCards ? (
          <Box sx={{ display: 'grid', gap: 2 }}>
            {paginatedPayments.map((payment) => (
              <ResponsiveRecordCard
                key={payment.id || payment.paymentIntentId}
                eyebrow="Payment"
                title={payment.paymentIntentId || 'Reference'}
                badge={
                  <Chip
                    label={capitalise(payment.status || 'Pending')}
                    color={getStatusColor(payment.status) as any}
                    size="small"
                  />
                }
                fields={[
                  {
                    label: 'Date',
                    value: (
                      <Typography variant="body2">
                        {payment.paymentDate
                          ? new Date(payment.paymentDate).toLocaleDateString('en-GB', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : 'N/A'}
                      </Typography>
                    ),
                  },
                  {
                    label: 'Purpose',
                    value: (
                      <Typography variant="body2">{capitalise(payment.purpose || '')}</Typography>
                    ),
                  },
                  {
                    label: 'Amount',
                    value: (
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                        {formatCurrency((payment.amount || 0) / 100)}
                      </Typography>
                    ),
                  },
                  {
                    label: 'Method',
                    value: (
                      <Typography variant="body2">
                        {capitalise(payment.paymentMethod || 'Online')}
                      </Typography>
                    ),
                  },
                ]}
              />
            ))}
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Purpose</TableCell>
                  <TableCell>Payment Method</TableCell>
                  <TableCell align="right">Amount ($)</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedPayments.map((payment) => (
                  <TableRow key={payment.id || payment.paymentIntentId}>
                    <TableCell>
                      {payment.paymentDate
                        ? new Date(payment.paymentDate).toLocaleDateString('en-GB', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : 'N/A'}
                    </TableCell>
                    <TableCell>{payment.paymentIntentId || 'N/A'}</TableCell>
                    <TableCell>{capitalise(payment.purpose || '')}</TableCell>
                    <TableCell>{capitalise(payment.paymentMethod || 'Online')}</TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 700, color: '#2e7d32' }}
                    >
                      {formatCurrency((payment.amount || 0) / 100)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={<CircleIcon sx={{ fontSize: '10px !important' }} />}
                        label={capitalise(payment.status || 'Pending')}
                        color={getStatusColor(payment.status) as any}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {filteredPayments.length > 0 && (
          <TablePagination
            component="div"
            count={filteredPayments.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        )}
      </Stack>
    </Paper>
  );
};

export default PadelCourtPaymentHistory;
